import { FC, ReactNode } from 'react';
import { DataRoomAccessPermission, DataRoomAccessStatus } from 'shared';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { DataRoomAccessAction, DataRoomAccessRow } from '../../../../../hooks/space/index';
import { StringKey } from '../../../../../lang/index';
import { stakeholderTypeTitle } from '../../../../../types/stakeholder.types';
import { LastActive } from '../last-active';
import { AccessAvatar } from './access-avatar';
import { AccessStatusBadge, RecipientKindLabel } from './access-badges';
import { AccessRowMenu } from './access-row-menu';
import { PERMISSION_LABEL, PermissionSelect } from './permission-select';

const EMPTY_CELL = '–';

const COLUMNS: { label: '' | StringKey; width: string }[] = [
  { label: 'NAME', width: 'w-[226px]' },
  { label: 'ORGANISATION', width: 'w-[154px]' },
  { label: 'TYPE', width: 'w-[129px]' },
  { label: 'STAKEHOLDER', width: 'w-[145px]' },
  { label: 'LAST_ACTIVE', width: 'w-[129px]' },
  { label: 'PERMISSIONS', width: 'w-[230px]' },
  { label: 'STATUS', width: 'w-[128px]' },
  { label: '', width: 'w-12' },
];

export type AccessTableProps = {
  footer?: ReactNode;
  isEditable: boolean;
  onAction: (row: DataRoomAccessRow, action: DataRoomAccessAction) => void;
  onPermissionChange: (row: DataRoomAccessRow, permission: DataRoomAccessPermission) => void;
  // The permission currently shown for a row: the pending local edit if there is one.
  permissionFor: (row: DataRoomAccessRow) => DataRoomAccessPermission;
  records: DataRoomAccessRow[];
};

export const AccessTable: FC<AccessTableProps> = ({
  footer,
  isEditable,
  onAction,
  onPermissionChange,
  permissionFor,
  records,
}) => (
  // `w-full`, not `w-fit`, and every cell able to shrink. The widths below are a width to divide,
  // not a width to insist on: they run past the 1151px the modal gives the table, and the name
  // column — the only flexible one — gives up the difference, as it also does to a vertical
  // scrollbar beside eleven rows. Insisting instead would scroll the whole table sideways. The
  // border sits inside that width, and the name column absorbs its two pixels; a ring would be
  // painted over by the grey header cells and vanish along the top.
  <div className="w-full overflow-hidden rounded-md border border-gray-100">
    <div className="flex items-start">
      {/* The menu column is the one without a heading, and a paused room has no row actions to
          put in it, so it goes away entirely rather than standing empty beside every row. */}
      {COLUMNS.filter(({ label }) => isEditable || label).map(({ label, width }, index) => (
        <div
          // Mirrors the body: the name column is the one that gives ground, so the header must
          // give exactly the same ground or the two stop lining up.
          className={`flex h-11 items-center border-b border-[#F2F2F2] bg-gray-50 px-4 py-3 ${width} ${
            index === 0 ? 'min-w-0 flex-1' : 'shrink-0'
          }`}
          key={label || 'menu'}
        >
          {label && (
            <span className="text-label-md font-medium text-[#172335]">
              <AppFormattedMessage id={label} />
            </span>
          )}
        </div>
      ))}
    </div>

    {records.map((row) => {
      // 20.2.4.1 section 3: an external recipient who has not finished signup has no name on
      // record, and the cell says so with a dash rather than repeating the address that is
      // already on the line beneath it. Their real name replaces it once they accept.
      const hasName = Boolean(row.name);

      return (
        <div className="flex items-start" key={row.id}>
          <div className="flex h-[72px] w-[226px] min-w-0 flex-1 items-center gap-2 border-b border-gray-100 p-4">
            <AccessAvatar label={row.name ?? row.email} src={row.avatarUrl} />
            <div className="flex min-w-px flex-1 flex-col justify-center">
              <p
                className={`truncate text-sm font-[450] leading-5 ${
                  hasName ? 'text-gray-700' : 'text-gray-400'
                }`}
              >
                {hasName ? row.name : EMPTY_CELL}
              </p>
              <p className="truncate text-label-sm font-medium leading-4 text-gray-500">
                {row.email}
              </p>
            </div>
          </div>

          <div className="flex h-[72px] w-[154px] shrink-0 items-center border-b border-gray-100 p-4">
            <p className="truncate text-sm font-[450] text-gray-700">{row.organisation}</p>
          </div>

          <div className="flex h-[72px] w-[129px] shrink-0 items-center border-b border-gray-100 p-4">
            {row.type ? (
              <p className="truncate text-sm font-[450] text-gray-700">
                <AppFormattedMessage id={stakeholderTypeTitle[row.type]} />
              </p>
            ) : (
              <p className="text-sm font-[450] text-gray-400">{EMPTY_CELL}</p>
            )}
          </div>

          <div className="flex h-[72px] w-[145px] shrink-0 items-center border-b border-gray-100 p-4">
            <p className="text-sm font-[450] text-gray-700">
              <RecipientKindLabel recipientKind={row.recipientKind} />
            </p>
          </div>

          <div className="flex h-[72px] w-[129px] shrink-0 items-center border-b border-gray-100 p-4">
            <p
              className={`text-sm font-[450] ${
                row.lastActiveAt && isEditable ? 'text-gray-700' : 'text-gray-400'
              }`}
            >
              <LastActive at={row.lastActiveAt} />
            </p>
          </div>

          {/* A control implies a choice. While the room is paused there is none to make, so the
              permission reads as the plain line of text it is, not as a select nobody can open. */}
          <div className="flex h-[72px] w-[230px] shrink-0 items-center border-b border-gray-100 p-4">
            {isEditable ? (
              <PermissionSelect
                isDisabled={row.status === DataRoomAccessStatus.REVOKED}
                onChange={(permission) => onPermissionChange(row, permission)}
                value={permissionFor(row)}
              />
            ) : (
              <p className="truncate text-sm font-[450] text-gray-400">
                <AppFormattedMessage id={PERMISSION_LABEL[permissionFor(row)]} />
              </p>
            )}
          </div>

          <div className="flex h-[72px] w-[128px] shrink-0 items-center border-b border-gray-100 p-4">
            <AccessStatusBadge status={row.status} />
          </div>

          {/* The only column that must not give anything up: it is already just the dots. It is
              also the first to go when the room is paused and there is nothing to act on. */}
          {isEditable && (
            <div className="flex h-[72px] w-12 shrink-0 items-center justify-center border-b border-[#F2F2F2] p-4">
              <AccessRowMenu onAction={(action) => onAction(row, action)} status={row.status} />
            </div>
          )}
        </div>
      );
    })}
    {footer}
  </div>
);
